import React, { useState, useEffect } from "react";

interface ServiceType {
  id: number;
  name: string;
  category: "consultation" | "machine" | "treatment";
  description?: string;
  duration: number;
  requires_equipment: boolean;
}

interface Equipment {
  id: number;
  name: string;
  type: "laser" | "EMS" | "Therapy" | "other";
  status: "available" | "maintenance" | "broken" | "retired";
  owner: { id: number; first_name: string; last_name: string };
  operator?: { id: number; first_name: string; last_name: string };
}

interface Provider {
  id: number;
  first_name: string;
  last_name: string;
  role: "doctor" | "technician";
  email: string;
}

interface MedicalService {
  id: number;
  name: string;
  description?: string;
  base_fee: number;
  clinic_percentage: number;
  provider_percentage: number;
  owner_percentage: number;
  referrer_percentage: number;
  charity_percentage: number;
  is_active: boolean;
  service_type: ServiceType;
  provider: Provider;
  equipment?: Equipment;
}

interface ServiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingService?: MedicalService | null;
  serviceTypes: ServiceType[];
  providers: Provider[];
  equipment: Equipment[];
  existingServices?: MedicalService[]; // ADD: To show which equipment is already used
}

const ServiceForm: React.FC<ServiceFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingService,
  serviceTypes,
  providers,
  equipment,
  existingServices = [], // ADD: Default empty array
}) => {
  const [formData, setFormData] = useState({
    service_type_id: "",
    provider_id: "",
    equipment_id: "",
    name: "",
    description: "",
    base_fee: "",
    clinic_percentage: "",
    provider_percentage: "",
    owner_percentage: "",
    referrer_percentage: "",
    charity_percentage: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedServiceType, setSelectedServiceType] =
    useState<ServiceType | null>(null);

  // Color theme
  const colorTheme = {
    primary: "#667eea",
    success: "#10b981",
    error: "#ef4444",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  // ADD: Helper function to get services using specific equipment
  const getServicesUsingEquipment = (equipmentId: number): MedicalService[] => {
    return existingServices.filter(
      (service) =>
        service.equipment?.id === equipmentId &&
        service.id !== editingService?.id // Exclude current service if editing
    );
  };

  // ADD: Helper function to generate automatic service name
  const generateServiceName = (
    serviceTypeName: string,
    providerName: string,
    equipmentName?: string
  ) => {
    if (equipmentName) {
      return `${equipmentName} - ${providerName}`;
    }
    return serviceTypeName;
  };

  // Get default revenue rules based on service category
  const getDefaultRevenueRules = (category: string) => {
    const rules = {
      consultation: {
        clinic_percentage: "65",
        provider_percentage: "30",
        owner_percentage: "0",
        referrer_percentage: "0",
        charity_percentage: "5",
      },
      machine: {
        clinic_percentage: "45",
        provider_percentage: "30",
        owner_percentage: "20",
        referrer_percentage: "0",
        charity_percentage: "5",
      },
      treatment: {
        clinic_percentage: "50",
        provider_percentage: "30",
        owner_percentage: "15",
        referrer_percentage: "0",
        charity_percentage: "5",
      },
    };

    return rules[category as keyof typeof rules] || rules.consultation;
  };

  // Initialize form when editing or when service type changes
  useEffect(() => {
    if (editingService) {
      setFormData({
        service_type_id: editingService.service_type.id.toString(),
        provider_id: editingService.provider.id.toString(),
        equipment_id: editingService.equipment?.id.toString() || "",
        name: editingService.name,
        description: editingService.description || "",
        base_fee: editingService.base_fee.toString(),
        clinic_percentage: editingService.clinic_percentage.toString(),
        provider_percentage: editingService.provider_percentage.toString(),
        owner_percentage: editingService.owner_percentage.toString(),
        referrer_percentage: editingService.referrer_percentage.toString(),
        charity_percentage:
          editingService.charity_percentage?.toString() || "0",
      });
      setSelectedServiceType(editingService.service_type);
    } else {
      // Reset form for new service
      setFormData({
        service_type_id: "",
        provider_id: "",
        equipment_id: "",
        name: "",
        description: "",
        base_fee: "",
        clinic_percentage: "",
        provider_percentage: "",
        owner_percentage: "",
        referrer_percentage: "",
        charity_percentage: "",
      });
      setSelectedServiceType(null);
    }
    setErrors({});
  }, [editingService, isOpen]);

  // UPDATED: Handle service type change with automatic naming
  const handleServiceTypeChange = (serviceTypeId: string) => {
    const serviceType = serviceTypes.find(
      (st) => st.id.toString() === serviceTypeId
    );
    setSelectedServiceType(serviceType || null);

    if (serviceType) {
      const defaultRules = getDefaultRevenueRules(serviceType.category);
      setFormData((prev) => ({
        ...prev,
        service_type_id: serviceTypeId,
        ...defaultRules,
        name: !editingService ? serviceType.name : prev.name,
        equipment_id: serviceType.requires_equipment ? prev.equipment_id : "",
      }));
    }
  };

  // ADD: Handle provider change with automatic naming
  const handleProviderChange = (providerId: string) => {
    const provider = providers.find((p) => p.id.toString() === providerId);
    setFormData((prev) => {
      const newFormData = { ...prev, provider_id: providerId };

      // Auto-generate name if not editing and we have the required info
      if (!editingService && selectedServiceType && provider) {
        const selectedEquipment = equipment.find(
          (eq) => eq.id.toString() === prev.equipment_id
        );
        newFormData.name = generateServiceName(
          selectedServiceType.name,
          `${provider.first_name} ${provider.last_name}`,
          selectedEquipment?.name
        );
      }

      return newFormData;
    });
  };

  // ADD: Handle equipment change with automatic naming
  const handleEquipmentChange = (equipmentId: string) => {
    setFormData((prev) => {
      const newFormData = { ...prev, equipment_id: equipmentId };

      // Auto-generate name if not editing and we have the required info
      if (!editingService && selectedServiceType) {
        const provider = providers.find(
          (p) => p.id.toString() === prev.provider_id
        );
        const selectedEquipment = equipment.find(
          (eq) => eq.id.toString() === equipmentId
        );

        if (provider) {
          newFormData.name = generateServiceName(
            selectedServiceType.name,
            `${provider.first_name} ${provider.last_name}`,
            selectedEquipment?.name
          );
        }
      }

      return newFormData;
    });
  };

  // Calculate revenue split preview
  const calculateRevenueSplit = () => {
    const baseFee = parseFloat(formData.base_fee) || 0;
    const clinic = parseFloat(formData.clinic_percentage) || 0;
    const provider = parseFloat(formData.provider_percentage) || 0;
    const owner = parseFloat(formData.owner_percentage) || 0;
    const referrer = parseFloat(formData.referrer_percentage) || 0;
    const charity = parseFloat(formData.charity_percentage) || 0;

    return {
      clinic: ((baseFee * clinic) / 100).toFixed(2),
      provider: ((baseFee * provider) / 100).toFixed(2),
      owner: ((baseFee * owner) / 100).toFixed(2),
      referrer: ((baseFee * referrer) / 100).toFixed(2),
      charity: ((baseFee * charity) / 100).toFixed(2),
      total: clinic + provider + owner + referrer + charity,
    };
  };

  const revenueSplit = calculateRevenueSplit();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const method = editingService ? "PUT" : "POST";
      const url = editingService
        ? `${API_BASE}/medical-services/${editingService.id}`
        : `${API_BASE}/medical-services`;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({
          ...formData,
          base_fee: parseFloat(formData.base_fee),
          clinic_percentage: parseInt(formData.clinic_percentage),
          provider_percentage: parseInt(formData.provider_percentage),
          owner_percentage: parseInt(formData.owner_percentage),
          referrer_percentage: parseInt(formData.referrer_percentage),
          charity_percentage: parseInt(formData.charity_percentage),
        }),
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ submit: errorData.error || "Failed to save service" });
        }
      }
    } catch (error) {
      console.error("Error saving service:", error);
      setErrors({ submit: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: colorTheme.cardBg,
          borderRadius: "16px",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: `1px solid ${colorTheme.gray200}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: colorTheme.gray900,
                margin: "0 0 4px 0",
              }}
            >
              {editingService ? "Edit Service" : "Add New Service"}
            </h3>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "14px",
                margin: 0,
              }}
            >
              Configure service details and revenue sharing
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "none",
              background: colorTheme.gray100,
              color: colorTheme.gray500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: "32px" }}>
          {/* Service Type & Provider */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Service"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Service Type <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.service_type_id}
                onChange={(e) => handleServiceTypeChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.service_type_id
                      ? colorTheme.error
                      : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                <option value="">Select service type...</option>
                {serviceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.category})
                  </option>
                ))}
              </select>
              {errors.service_type_id && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.service_type_id}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="Provider"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Provider <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.provider_id}
                onChange={(e) => handleProviderChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.provider_id ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                <option value="">Select provider...</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.first_name} {provider.last_name} ({provider.role})
                  </option>
                ))}
              </select>
              {errors.provider_id && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.provider_id}
                </p>
              )}
            </div>
          </div>

          {/* UPDATED: Equipment (if required) - Now shows all functional equipment */}
          {selectedServiceType?.requires_equipment && (
            <div style={{ marginBottom: "24px" }}>
              <label
                htmlFor="Equipment"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Equipment <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.equipment_id}
                onChange={(e) => handleEquipmentChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.equipment_id ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                <option value="">Select equipment...</option>
                {equipment
                  .filter(
                    (eq) =>
                      eq.status === "available" || eq.status === "maintenance"
                  ) // CHANGED: Allow available and maintenance
                  .map((eq) => {
                    const servicesUsingThis = getServicesUsingEquipment(eq.id);
                    const isCurrentlyUsed = servicesUsingThis.length > 0;

                    return (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} ({eq.type})
                        {isCurrentlyUsed &&
                          ` - Used by ${
                            servicesUsingThis.length
                          } other service${
                            servicesUsingThis.length > 1 ? "s" : ""
                          }`}
                        {eq.status === "maintenance" && " - Under Maintenance"}
                      </option>
                    );
                  })}
              </select>

              {/* ADDED: Show equipment usage information */}
              {formData.equipment_id && (
                <div style={{ marginTop: "8px" }}>
                  {(() => {
                    const selectedEquipment = equipment.find(
                      (eq) => eq.id.toString() === formData.equipment_id
                    );
                    const servicesUsingThis = getServicesUsingEquipment(
                      parseInt(formData.equipment_id)
                    );

                    if (servicesUsingThis.length > 0) {
                      return (
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#fef3c7",
                            border: "1px solid #fbbf24",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                          }}
                        >
                          <strong>Shared Equipment:</strong> This equipment is
                          also used by:
                          <ul
                            style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}
                          >
                            {servicesUsingThis.map((service) => (
                              <li key={service.id}>
                                {service.name} (Dr.{" "}
                                {service.provider.first_name}{" "}
                                {service.provider.last_name})
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    } else if (selectedEquipment?.status === "maintenance") {
                      return (
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#fef3c7",
                            border: "1px solid #fbbf24",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                          }}
                        >
                          <strong>Under Maintenance:</strong> This equipment is
                          currently under maintenance but can still be assigned
                          to services.
                        </div>
                      );
                    } else {
                      return (
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#ecfdf5",
                            border: "1px solid #10b981",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#065f46",
                          }}
                        >
                          <strong>Dedicated Equipment:</strong> This equipment
                          will be exclusively used for this service.
                        </div>
                      );
                    }
                  })()}
                </div>
              )}

              {errors.equipment_id && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.equipment_id}
                </p>
              )}
            </div>
          )}

          {/* Service Name & Description */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Service"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Service Name <span style={{ color: colorTheme.error }}>*</span>
            </label>
            <input
              type="text"
              id="service_name"
              name="service_name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.name ? colorTheme.error : colorTheme.gray200
                }`,
                borderRadius: "8px",
                fontSize: "14px",
              }}
              placeholder="Enter service name..."
              required
            />

            {errors.name && (
              <p
                style={{
                  color: colorTheme.error,
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                {errors.name}
              </p>
            )}
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Description"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                fontSize: "14px",
                resize: "none",
              }}
              placeholder="Optional service description..."
            />
          </div>

          {/* Base Fee */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Base"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Base Fee <span style={{ color: colorTheme.error }}>*</span>
            </label>
            <input
              type="number"
              id="base_fee"
              name="base_fee"
              value={formData.base_fee}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, base_fee: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.base_fee ? colorTheme.error : colorTheme.gray200
                }`,
                borderRadius: "8px",
                fontSize: "14px",
              }}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />

            {errors.base_fee && (
              <p
                style={{
                  color: colorTheme.error,
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                {errors.base_fee}
              </p>
            )}
          </div>

          {/* Revenue Sharing */}
          <div
            style={{
              background: colorTheme.gray50,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: colorTheme.gray900,
                marginBottom: "16px",
              }}
            >
              Revenue Sharing Configuration
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  htmlFor="Clinic"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Clinic Percentage
                </label>
                <input
                  type="number"
                  id="clinic_percentage"
                  name="clinic_percentage"
                  value={formData.clinic_percentage}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      clinic_percentage:
                        e.target.value === "" ? "0" : e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <label
                  htmlFor="Provider"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Provider Percentage
                </label>
                <input
                  type="number"
                  id="provider_percentage"
                  name="provider_percentage"
                  value={formData.provider_percentage}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      provider_percentage:
                        e.target.value === "" ? "0" : e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <label
                  htmlFor="Sponsor"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Sponsor Percentage
                </label>
                <input
                  type="number"
                  id="owner_percentage"
                  name="owner_percentage"
                  value={formData.owner_percentage}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      owner_percentage:
                        e.target.value === "" ? "0" : e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <label
                  htmlFor="Referrer"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Referrer Percentage
                </label>
                <input
                  type="number"
                  id="referrer_percentage"
                  name="referrer_percentage"
                  value={formData.referrer_percentage}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      referrer_percentage:
                        e.target.value === "" ? "0" : e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <label
                  htmlFor="Charity"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Charity Percentage
                </label>
                <input
                  type="number"
                  id="charity_percentage"
                  name="charity_percentage"
                  value={formData.charity_percentage}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      charity_percentage:
                        e.target.value === "" ? "0" : e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Revenue Preview */}
            {formData.base_fee && (
              <div
                style={{
                  background: colorTheme.cardBg,
                  borderRadius: "8px",
                  padding: "16px",
                  border: `2px solid ${
                    revenueSplit.total === 100
                      ? colorTheme.success
                      : colorTheme.error
                  }`,
                }}
              >
                <h5
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "12px",
                  }}
                >
                  Revenue Split Preview (${formData.base_fee})
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: "12px",
                    fontSize: "13px",
                  }}
                >
                  <div>
                    <span style={{ color: colorTheme.gray600 }}>Clinic:</span>
                    <div style={{ fontWeight: "600" }}>
                      ${revenueSplit.clinic}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: colorTheme.gray600 }}>Provider:</span>
                    <div style={{ fontWeight: "600" }}>
                      ${revenueSplit.provider}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: colorTheme.gray600 }}>Owner:</span>
                    <div style={{ fontWeight: "600" }}>
                      ${revenueSplit.owner}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: colorTheme.gray600 }}>Referrer:</span>
                    <div style={{ fontWeight: "600" }}>
                      ${revenueSplit.referrer}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: colorTheme.gray600 }}>Charity:</span>
                    <div style={{ fontWeight: "600" }}>
                      ${revenueSplit.charity}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px",
                    borderRadius: "6px",
                    background:
                      revenueSplit.total === 100 ? "#dcfce7" : "#fecaca",
                    color: revenueSplit.total === 100 ? "#166534" : "#991b1b",
                    fontSize: "12px",
                    textAlign: "center",
                    fontWeight: "500",
                  }}
                >
                  Total: {revenueSplit.total}%{" "}
                  {revenueSplit.total === 100 ? "✓" : "⚠️ Must equal 100%"}
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fecaca",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "14px",
                marginBottom: "24px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "20px",
              borderTop: `1px solid ${colorTheme.gray200}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "12px 24px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                background: colorTheme.cardBg,
                color: colorTheme.gray600,
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || revenueSplit.total !== 100}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background:
                  loading || revenueSplit.total !== 100
                    ? colorTheme.gray200
                    : colorTheme.success,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor:
                  loading || revenueSplit.total !== 100
                    ? "not-allowed"
                    : "pointer",
                opacity: loading || revenueSplit.total !== 100 ? 0.6 : 1,
              }}
            >
              {loading
                ? "Saving..."
                : editingService
                ? "Update Service"
                : "Create Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm;
